import React, { useState, useMemo } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { SimpleDropdown } from './ui/simple-dropdown';
import { X, Save, User, Mail, Phone, Eye, EyeOff, CheckCircle } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { educationAPI } from '../api/axiosClient';
import { IMaskInput } from 'react-imask';

interface SystemAdminCreateFormProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

const SystemAdminCreateForm: React.FC<SystemAdminCreateFormProps> = ({
  isOpen,
  onClose,
  onSuccess
}) => {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phoneCode: '90',
    phoneNumber: '',
    password: '',
    twoFactorEnabled: false,
    requirePasswordResetOnFirstLogin: true
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showPassword, setShowPassword] = useState<boolean>(false);
  const [errors, setErrors] = useState<{[key: string]: string}>({});

  // Country codes for phone dropdown
  const countryCodes = useMemo(() => [
    { code: '90', name: 'Türkiye (+90)', flag: '🇹🇷' },
    { code: '1', name: 'USA (+1)', flag: '🇺🇸' },
    { code: '44', name: 'UK (+44)', flag: '🇬🇧' },
    { code: '49', name: 'Germany (+49)', flag: '🇩🇪' },
    { code: '33', name: 'France (+33)', flag: '🇫🇷' },
    { code: '39', name: 'Italy (+39)', flag: '🇮🇹' },
    { code: '34', name: 'Spain (+34)', flag: '🇪🇸' },
    { code: '31', name: 'Netherlands (+31)', flag: '🇳🇱' },
    { code: '32', name: 'Belgium (+32)', flag: '🇧🇪' },
    { code: '46', name: 'Sweden (+46)', flag: '🇸🇪' },
    { code: '47', name: 'Norway (+47)', flag: '🇳🇴' },
    { code: '45', name: 'Denmark (+45)', flag: '🇩🇰' },
    { code: '358', name: 'Finland (+358)', flag: '🇫🇮' },
    { code: '7', name: 'Russia (+7)', flag: '🇷🇺' },
    { code: '86', name: 'China (+86)', flag: '🇨🇳' },
    { code: '81', name: 'Japan (+81)', flag: '🇯🇵' },
    { code: '82', name: 'South Korea (+82)', flag: '🇰🇷' },
    { code: '91', name: 'India (+91)', flag: '🇮🇳' },
    { code: '971', name: 'UAE (+971)', flag: '🇦🇪' },
    { code: '966', name: 'Saudi Arabia (+966)', flag: '🇸🇦' },
    { code: '20', name: 'Egypt (+20)', flag: '🇪🇬' },
    { code: '27', name: 'South Africa (+27)', flag: '🇿🇦' },
    { code: '55', name: 'Brazil (+55)', flag: '🇧🇷' },
    { code: '54', name: 'Argentina (+54)', flag: '🇦🇷' },
    { code: '52', name: 'Mexico (+52)', flag: '🇲🇽' }
  ], []);


  // Dynamic phone mask based on country code
  const getPhoneMask = (countryCode: string): string => {
    switch (countryCode) {
      case '90': // Türkiye
        return '(000) 000-0000';
      case '1': // USA
        return '(000) 000-0000';
      case '44': // UK
        return '0000 000000';
      case '49': // Germany
        return '000 00000000';
      case '33': // France
        return '0 00 00 00 00';
      case '39': // Italy
        return '000 000 0000';
      case '34': // Spain
        return '000 000 000';
      case '31': // Netherlands
        return '0 00000000';
      case '32': // Belgium
        return '000 000 000';
      case '46': // Sweden
        return '000 000 000';
      case '47': // Norway
        return '000 00 000';
      case '45': // Denmark
        return '00 00 00 00';
      case '358': // Finland
        return '000 000 0000';
      case '7': // Russia
        return '000 000-00-00';
      case '86': // China
        return '000 0000 0000';
      case '81': // Japan
        return '00-0000-0000';
      case '82': // South Korea
        return '000-0000-0000';
      case '91': // India
        return '00000 00000';
      case '971': // UAE
        return '000 000 000';
      case '966': // Saudi Arabia
        return '000 000 000';
      case '20': // Egypt
        return '000 000 0000';
      case '27': // South Africa
        return '000 000 0000';
      case '55': // Brazil
        return '(00) 00000-0000';
      case '54': // Argentina
        return '000 000-0000';
      case '52': // Mexico
        return '000 000 0000';
      default: return '0000000000';
    }
  };

  // Get placeholder based on country code
  const getPhonePlaceholder = (countryCode: string): string => {
    switch (countryCode) {
      case '90': return '(5XX) XXX-XXXX';
      case '1': return '(XXX) XXX-XXXX';
      case '44': return 'XXXX XXXXXX';
      case '49': return 'XXX XXXXXXXX';
      case '33': return 'X XX XX XX XX';
      case '39': return 'XXX XXX XXXX';
      case '34': return 'XXX XXX XXX';
      case '31': return 'X XXXXXXXX';
      case '32': return 'XXX XXX XXX';
      case '46': return 'XXX XXX XXX';
      case '47': return 'XXX XX XXX';
      case '45': return 'XX XX XX XX';
      case '358': return 'XXX XXX XXXX';
      case '7': return 'XXX XXX-XX-XX';
      case '86': return 'XXX XXXX XXXX';
      case '81': return 'XX-XXXX-XXXX';
      case '82': return 'XXX-XXXX-XXXX';
      case '91': return 'XXXXX XXXXX';
      case '971': return 'XXX XXX XXX';
      case '966': return 'XXX XXX XXX';
      case '20': return 'XXX XXX XXXX';
      case '27': return 'XXX XXX XXXX';
      case '55': return '(XX) XXXXX-XXXX';
      case '54': return 'XXX XXX-XXXX';
      case '52': return 'XXX XXX XXXX';
      default: return 'XXXXXXXXXXXX';
    }
  };

  // Password validation
  const passwordValidation = useMemo(() => ({
    isNumeric: /^\d+$/.test(formData.password),
    isLength6: formData.password.length === 6
  }), [formData.password]);

  const handleInputChange = (field: string, value: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  const validateForm = (): boolean => {
    const newErrors: {[key: string]: string} = {};

    if (!formData.firstName?.trim()) {
      newErrors.firstName = 'Ad alanı zorunludur';
    }

    if (!formData.lastName?.trim()) {
      newErrors.lastName = 'Soyad alanı zorunludur';
    }

    if (!formData.email?.trim()) {
      newErrors.email = 'E-posta alanı zorunludur';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Geçerli bir e-posta adresi girin';
    }

    if (!formData.phoneNumber?.trim()) {
      newErrors.phoneNumber = 'Telefon numarası alanı zorunludur';
    }

    if (!formData.password?.trim()) {
      newErrors.password = 'Şifre alanı zorunludur';
    } else if (!passwordValidation.isNumeric) {
      newErrors.password = 'Şifre sadece rakam içermelidir';
    } else if (!passwordValidation.isLength6) {
      newErrors.password = 'Şifre 6 haneli olmalıdır';
    }


    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    try {
      const request = {
        firstName: formData.firstName,
        lastName: formData.lastName,
        email: formData.email,
        phoneCode: formData.phoneCode,
        phoneNumber: formData.phoneNumber,
        password: formData.password,
        twoFactorEnabled: formData.twoFactorEnabled,
        requirePasswordResetOnFirstLogin: formData.requirePasswordResetOnFirstLogin
      };

      const response = await educationAPI.createSystemAdmin(request);
      
      if (response.success) {
        toast.success('Sistem yöneticisi başarıyla eklendi');
        onSuccess();
        onClose();
        resetForm();
      } else {
        toast.error(response.message || 'Sistem yöneticisi eklenirken hata oluştu');
      }
    } catch (error: any) {
      console.error('Error creating system admin:', error);
      toast.error('Sistem yöneticisi eklenirken hata oluştu');
    } finally {
      setIsSubmitting(false);
    }
  };

  const resetForm = () => {
    setFormData({
      firstName: '',
      lastName: '',
      email: '',
      phoneCode: '90',
      phoneNumber: '',
      password: '',
      twoFactorEnabled: false,
      requirePasswordResetOnFirstLogin: true
    });
    setErrors({});
    setShowPassword(false);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <Card className="w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-4">
          <div>
            <CardTitle className="text-xl font-semibold">Yeni Sistem Yöneticisi Ekle</CardTitle>
            <CardDescription>
              Sistem yöneticisi bilgilerini girin
            </CardDescription>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={onClose}
            className="h-8 w-8 p-0"
          >
            <X className="h-4 w-4" />
          </Button>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Ad */}
              <div>
                <Label htmlFor="firstName" className="text-sm font-medium text-gray-700 mb-2 flex items-center">
                  <User className="w-4 h-4 mr-2" />
                  Ad <span className="text-red-500 ml-1">*</span>
                </Label>
                <Input
                  id="firstName"
                  type="text"
                  value={formData.firstName}
                  onChange={(e) => handleInputChange('firstName', e.target.value)}
                  className={errors.firstName ? 'border-red-500' : ''}
                  placeholder="Ad"
                />
                {errors.firstName && (
                  <p className="text-sm text-red-600 mt-1">{errors.firstName}</p>
                )}
              </div>

              {/* Soyad */}
              <div>
                <Label htmlFor="lastName" className="text-sm font-medium text-gray-700 mb-2 flex items-center">
                  <User className="w-4 h-4 mr-2" />
                  Soyad <span className="text-red-500 ml-1">*</span>
                </Label>
                <Input
                  id="lastName"
                  type="text"
                  value={formData.lastName}
                  onChange={(e) => handleInputChange('lastName', e.target.value)}
                  className={errors.lastName ? 'border-red-500' : ''}
                  placeholder="Soyad"
                />
                {errors.lastName && (
                  <p className="text-sm text-red-600 mt-1">{errors.lastName}</p>
                )}
              </div>

              {/* E-posta */}
              <div>
                <Label htmlFor="email" className="text-sm font-medium text-gray-700 mb-2 flex items-center">
                  <Mail className="w-4 h-4 mr-2" />
                  E-posta <span className="text-red-500 ml-1">*</span>
                </Label>
                <Input
                  id="email"
                  type="text"
                  value={formData.email}
                  onChange={(e) => handleInputChange('email', e.target.value)}
                  className={errors.email ? 'border-red-500' : ''}
                  placeholder="ornek@email.com"
                />
                {errors.email && (
                  <p className="text-sm text-red-600 mt-1">{errors.email}</p>
                )}
              </div>

              {/* Telefon */}
              <div>
                <Label className="text-sm font-medium text-gray-700 mb-2 flex items-center">
                  <Phone className="w-4 h-4 mr-2" />
                  Telefon <span className="text-red-500 ml-1">*</span>
                </Label>
                <div className="flex space-x-2">
                  <SimpleDropdown
                    options={countryCodes.map((code) => ({
                      id: code.code,
                      label: `${code.flag} +${code.code}`,
                      value: code.code
                    }))}
                    value={countryCodes.find(country => country.code === formData.phoneCode) ? {
                      id: formData.phoneCode,
                      label: `${countryCodes.find(country => country.code === formData.phoneCode)?.flag} +${formData.phoneCode}`,
                      value: formData.phoneCode
                    } : null}
                    onChange={(countryCode) => {
                      if (countryCode) {
                        handleInputChange('phoneCode', String(countryCode.value));
                        handleInputChange('phoneNumber', ''); // Reset phone number when country changes
                      }
                    }}
                    placeholder="Ülke seçiniz"
                    className="w-24"
                  />
                  <IMaskInput
                    mask={getPhoneMask(formData.phoneCode || '90')}
                    placeholder={getPhonePlaceholder(formData.phoneCode || '90')}
                    value={formData.phoneNumber || ''}
                    onAccept={(value: string) => {
                      handleInputChange('phoneNumber', value);
                    }}
                    className={`flex-1 text-left text-sm font-mono rounded-md border border-gray-300 bg-white px-3 py-2 outline-none ring-2 ring-transparent transition focus:border-gray-400 focus:ring-gray-200 disabled:cursor-not-allowed disabled:bg-gray-100 hover:border-gray-400 ${errors.phoneNumber ? 'border-red-500' : ''}`}
                    inputMode="numeric"
                  />
                </div>
                {errors.phoneNumber && (
                  <p className="text-sm text-red-600 mt-1">{errors.phoneNumber}</p>
                )}
              </div>


              {/* Şifre */}
              <div>
                <Label htmlFor="password" className="text-sm font-medium text-gray-700 mb-2 flex items-center">
                  <Eye className="w-4 h-4 mr-2" />
                  Şifre <span className="text-red-500 ml-1">*</span>
                </Label>
                <div className="relative">
                  <Input
                    id="password"
                    type={showPassword ? "text" : "password"}
                    inputMode="numeric"
                    pattern="[0-9]*"
                    maxLength={6}
                    value={formData.password}
                    onChange={(e) => handleInputChange('password', e.target.value)}
                    className={`pr-10 ${errors.password ? 'border-red-500' : ''}`}
                    placeholder="••••••"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
                
                {/* Password Strength Indicator */}
                {formData.password && (
                  <div className="space-y-2 mt-2">
                    <div className="flex space-x-2">
                      <div className={`w-1/2 h-2 rounded-full ${
                        passwordValidation.isNumeric ? 'bg-green-500' : 'bg-gray-200'
                      }`}></div>
                      <div className={`w-1/2 h-2 rounded-full ${
                        passwordValidation.isLength6 ? 'bg-green-500' : 'bg-gray-200'
                      }`}></div>
                    </div>
                    <div className="text-xs text-gray-500 space-y-1">
                      <div className={`flex items-center space-x-2 ${passwordValidation.isNumeric ? 'text-green-600' : 'text-gray-400'}`}>
                        <div className={`w-2 h-2 rounded-full ${passwordValidation.isNumeric ? 'bg-green-500' : 'bg-gray-300'}`}></div>
                        <span>Sadece rakam</span>
                      </div>
                      <div className={`flex items-center space-x-2 ${passwordValidation.isLength6 ? 'text-green-600' : 'text-gray-400'}`}>
                        <div className={`w-2 h-2 rounded-full ${passwordValidation.isLength6 ? 'bg-green-500' : 'bg-gray-300'}`}></div>
                        <span>6 haneli</span>
                      </div>
                    </div>
                  </div>
                )}
                {errors.password && (
                  <p className="text-sm text-red-600 mt-1">{errors.password}</p>
                )}
              </div>
            </div>

            {/* İki Faktörlü Kimlik Doğrulama ve Şifre Sıfırlama */}
            <div className="space-y-4">
              <div className="flex items-center space-x-3">
                <input
                  type="checkbox"
                  id="twoFactorEnabled"
                  checked={formData.twoFactorEnabled}
                  onChange={(e) => handleInputChange('twoFactorEnabled', e.target.checked)}
                  className="w-4 h-4 text-[#9B51E0] bg-gray-100 border-gray-300 rounded focus:ring-[#9B51E0] focus:ring-2"
                />
                <label htmlFor="twoFactorEnabled" className="flex items-center space-x-2 text-sm font-medium text-gray-700">
                  <CheckCircle className="w-4 h-4 text-gray-500" />
                  <span>İki Faktörlü Kimlik Doğrulama Etkin</span>
                </label>
              </div>
              
              <div className="flex items-center space-x-3">
                <input
                  type="checkbox"
                  id="requirePasswordResetOnFirstLogin"
                  checked={formData.requirePasswordResetOnFirstLogin}
                  onChange={(e) => handleInputChange('requirePasswordResetOnFirstLogin', e.target.checked)}
                  className="w-4 h-4 text-[#9B51E0] bg-gray-100 border-gray-300 rounded focus:ring-[#9B51E0] focus:ring-2"
                />
                <label htmlFor="requirePasswordResetOnFirstLogin" className="flex items-center space-x-2 text-sm font-medium text-gray-700">
                  <CheckCircle className="w-4 h-4 text-gray-500" />
                  <span>İlk Girişte Şifre Sıfırlama Gerekli</span>
                </label>
              </div>
            </div>

            <div className="flex justify-end space-x-3 pt-6 border-t">
              <Button
                type="button"
                variant="outline"
                onClick={onClose}
                disabled={isSubmitting}
              >
                İptal
              </Button>
              <Button
                type="submit"
                disabled={isSubmitting}
                className="bg-[#9B51E0] hover:bg-[#8B4AD9] text-white"
              >
                {isSubmitting ? (
                  <div className="flex items-center">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Ekleniyor...
                  </div>
                ) : (
                  <div className="flex items-center">
                    <Save className="w-4 h-4 mr-2" />
                    Kaydet
                  </div>
                )}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

export default SystemAdminCreateForm;
